import { Modal, Input, Empty, Tag } from 'antd';
import type { InputRef } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search,
  CornerDownLeft,
  ArrowUp,
  ArrowDown,
  Command as CommandIcon,
  LayoutDashboard,
  FileText,
  HelpCircle,
  Tag as TagIcon,
  Users,
  ClipboardList,
  Settings,
  Moon,
  Sun,
  LogOut,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { ROUTES } from '@shared/constants/routes';
import { useUiStore, resolveIsDark } from '@shared/stores/uiStore';
import { useAuthStore } from '@features/auth/store';

export interface CommandItem {
  id: string;
  label: string;
  group: string;
  keywords?: string;
  icon?: ReactNode;
  shortcut?: string;
  action: () => void;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

function fuzzyMatch(needle: string, haystack: string): boolean {
  if (!needle) return true;
  const n = needle.toLocaleLowerCase('tr-TR');
  const h = haystack.toLocaleLowerCase('tr-TR');
  let i = 0;
  for (const ch of n) {
    const idx = h.indexOf(ch, i);
    if (idx < 0) return false;
    i = idx + 1;
  }
  return true;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const { t } = useTranslation('components');
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<InputRef | null>(null);

  const colorScheme = useUiStore((s) => s.colorScheme);
  const setColorScheme = useUiStore((s) => s.setColorScheme);
  const clearSession = useAuthStore((s) => s.clearSession);

  const go = (to: string) => {
    void navigate(to);
    onClose();
  };

  const commands = useMemo<CommandItem[]>(
    () => [
      // Navigation
      { id: 'nav-dashboard', group: t('commandPalette.groups.navigation'), label: t('commandPalette.items.dashboard'), icon: <LayoutDashboard size={15} />, action: () => go(ROUTES.DASHBOARD), keywords: 'home anasayfa overview' },
      { id: 'nav-articles', group: t('commandPalette.groups.navigation'), label: t('commandPalette.items.articles'), icon: <FileText size={15} />, action: () => go(ROUTES.ARTICLES), keywords: 'blog article post' },
      { id: 'nav-article-new', group: t('commandPalette.groups.quickAction'), label: t('commandPalette.items.articleNew'), icon: <FileText size={15} />, action: () => go(ROUTES.ARTICLE_NEW), keywords: 'create new yazı' },
      { id: 'nav-pages', group: t('commandPalette.groups.navigation'), label: t('commandPalette.items.pages'), icon: <FileText size={15} />, action: () => go(ROUTES.PAGES), keywords: 'sayfa page statik hakkımızda kvkk' },
      { id: 'nav-sss', group: t('commandPalette.groups.navigation'), label: t('commandPalette.items.sss'), icon: <HelpCircle size={15} />, action: () => go(ROUTES.SSS), keywords: 'faq sorular' },
      { id: 'nav-categories', group: t('commandPalette.groups.taxonomy'), label: t('commandPalette.items.categories'), icon: <TagIcon size={15} />, action: () => go(ROUTES.CATEGORIES) },
      { id: 'nav-tags', group: t('commandPalette.groups.taxonomy'), label: t('commandPalette.items.tags'), icon: <TagIcon size={15} />, action: () => go(ROUTES.TAGS) },
      { id: 'nav-authors', group: t('commandPalette.groups.taxonomy'), label: t('commandPalette.items.authors'), icon: <TagIcon size={15} />, action: () => go(ROUTES.AUTHORS) },
      { id: 'nav-admin-users', group: t('commandPalette.groups.user'), label: t('commandPalette.items.adminUsers'), icon: <Users size={15} />, action: () => go(ROUTES.ADMIN_USERS) },
      { id: 'nav-roles', group: t('commandPalette.groups.user'), label: t('commandPalette.items.roles'), icon: <Users size={15} />, action: () => go(ROUTES.ROLES) },
      { id: 'nav-kvkk', group: t('commandPalette.groups.user'), label: t('commandPalette.items.kvkk'), icon: <Users size={15} />, action: () => go(ROUTES.KVKK_DELETIONS) },
      { id: 'nav-audit', group: t('commandPalette.groups.system'), label: t('commandPalette.items.audit'), icon: <ClipboardList size={15} />, action: () => go(ROUTES.AUDIT_LOG), keywords: 'audit log' },
      { id: 'nav-settings', group: t('commandPalette.groups.system'), label: t('commandPalette.items.settings'), icon: <Settings size={15} />, action: () => go(ROUTES.SETTINGS) },
      { id: 'nav-profile', group: t('commandPalette.groups.system'), label: t('commandPalette.items.profile'), icon: <Users size={15} />, action: () => go(ROUTES.PROFILE) },

      // Theme
      {
        id: 'theme-toggle',
        group: t('commandPalette.groups.preferences'),
        label: resolveIsDark(colorScheme) ? t('commandPalette.items.themeLight') : t('commandPalette.items.themeDark'),
        icon: resolveIsDark(colorScheme) ? <Sun size={15} /> : <Moon size={15} />,
        shortcut: '⇧⌘L',
        action: () => {
          setColorScheme(resolveIsDark(colorScheme) ? 'light' : 'dark');
          onClose();
        },
      },
      {
        id: 'theme-system',
        group: t('commandPalette.groups.preferences'),
        label: t('commandPalette.items.themeSystem'),
        icon: <CommandIcon size={15} />,
        action: () => {
          setColorScheme('system');
          onClose();
        },
      },

      // Session
      {
        id: 'logout',
        group: t('commandPalette.groups.session'),
        label: t('commandPalette.items.logout'),
        icon: <LogOut size={15} />,
        action: () => {
          clearSession();
          void navigate(ROUTES.LOGIN, { replace: true });
          onClose();
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [colorScheme, t],
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return commands;
    return commands.filter((c) =>
      fuzzyMatch(query, `${c.label} ${c.group} ${c.keywords ?? ''}`),
    );
  }, [commands, query]);

  const grouped = useMemo(() => {
    const groups: Record<string, CommandItem[]> = {};
    filtered.forEach((c) => {
      const existing = groups[c.group] ?? [];
      existing.push(c);
      groups[c.group] = existing;
    });
    return groups;
  }, [filtered]);

  useEffect(() => {
    if (open) {
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-cmd-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, filtered.length]);

  const flat = filtered;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      flat[activeIndex]?.action();
    }
  };

  let runningIndex = -1;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={620}
      centered={false}
      style={{ top: 90 }}
      styles={{
        body: { padding: 0 },
        content: { padding: 0, borderRadius: 14, overflow: 'hidden' },
      }}
      destroyOnHidden
    >
      <div
        style={{
          padding: '14px 16px',
          borderBottom: '1px solid var(--ds-border-subtle)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <Search size={16} style={{ color: 'var(--ds-text-tertiary)' }} />
        <Input
          ref={inputRef}
          variant="borderless"
          placeholder={t('commandPalette.searchPlaceholder')}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIndex(0);
          }}
          onKeyDown={onKeyDown}
          style={{ fontSize: 15, padding: 0 }}
          aria-label={t('commandPalette.searchAriaLabel')}
        />
        <Tag style={{ marginInlineEnd: 0, fontSize: 11 }}>ESC</Tag>
      </div>

      <div
        ref={listRef}
        style={{ maxHeight: 420, overflow: 'auto', padding: 8 }}
        role="listbox"
        aria-label={t('commandPalette.commandsLabel')}
      >
        {flat.length === 0 ? (
          <div style={{ padding: 32 }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span style={{ color: 'var(--ds-text-tertiary)' }}>{t('commandPalette.empty')}</span>
              }
            />
          </div>
        ) : (
          Object.entries(grouped).map(([group, items]) => (
            <div key={group} style={{ marginBottom: 6 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: 'var(--ds-text-tertiary)',
                  padding: '8px 10px 4px',
                }}
              >
                {group}
              </div>
              {items.map((cmd) => {
                runningIndex += 1;
                const isActive = runningIndex === activeIndex;
                const idx = runningIndex;
                return (
                  <button
                    key={cmd.id}
                    type="button"
                    data-cmd-index={idx}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => cmd.action()}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      border: 0,
                      background: isActive ? 'var(--ds-brand-subtle)' : 'transparent',
                      borderRadius: 8,
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background var(--ds-motion-fast)',
                      color: 'var(--ds-text-primary)',
                    }}
                    role="option"
                    aria-selected={isActive}
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        width: 24,
                        height: 24,
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: isActive ? 'var(--ds-brand)' : 'var(--ds-text-secondary)',
                      }}
                    >
                      {cmd.icon}
                    </span>
                    <span style={{ flex: 1, fontSize: 14 }}>{cmd.label}</span>
                    {cmd.shortcut ? (
                      <Tag style={{ marginInlineEnd: 0, fontSize: 11 }}>{cmd.shortcut}</Tag>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ))
        )}
      </div>

      <div
        style={{
          borderTop: '1px solid var(--ds-border-subtle)',
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          fontSize: 11,
          color: 'var(--ds-text-tertiary)',
          background: 'var(--ds-bg-subtle)',
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <ArrowUp size={12} />
          <ArrowDown size={12} /> {t('commandPalette.navHint')}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <CornerDownLeft size={12} /> {t('commandPalette.selectHint')}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <kbd style={{ fontFamily: 'inherit' }}>esc</kbd> {t('commandPalette.closeHint')}
        </span>
        <span style={{ marginInlineStart: 'auto' }}>{flat.length} {t('commandPalette.resultCount')}</span>
      </div>
    </Modal>
  );
}
